import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Navbar from "../components/Navbar";
import "./css/CommonComplete.css";
import uaeFlag from "../assets/icons/country_code.png";
import onboardingCompletedImage from "../assets/images/success.png";

export default function ProfileStep1() {
  const navigate = useNavigate();
  const phoneRef = useRef(null);

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState({});
  const [throwError, setThrowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  const t = {
    en: {
      loading: "Loading",
      title: "User Details",
      subtitle: "Please enter the details to create your account",
      nameLabel: "Name *",
      namePlaceholder: "Your full name",
      nameError: "Please enter a valid name.",
      whatsappLabel: "WhatsApp Number",
      whatsappPlaceholder: "50 XXX XXXX",
      whatsappError: "Please enter a valid WhatsApp number.",
      emailLabel: "Email *",
      emailPlaceholder: "name@email.com",
      emailError: "Please enter a valid email address.",
      continue: "Continue",
      successTitle: "Onboarding Completed",
      successSubtitle: "Your account is created successfully, you can start using the app now.",
      successContinue: "Continue",
      somethingWrong: "Something went wrong!",
      failedToLoad: "Failed to load profile data",
    },
    ar: {
      loading: "جاري التحميل",
      title: "بيانات المستخدم",
      subtitle: "يرجى إدخال البيانات لإنشاء حسابك",
      nameLabel: "الاسم *",
      namePlaceholder: "ادخل اسمك الكامل",
      nameError: "يرجى إدخال اسم صحيح.",
      whatsappLabel: "رقم واتساب",
      whatsappPlaceholder: "50 XXX XXXX",
      whatsappError: "يرجى إدخال رقم واتساب صحيح.",
      emailLabel: "البريد الإلكتروني *",
      emailPlaceholder: "name@email.com",
      emailError: "يرجى إدخال بريد إلكتروني صحيح.",
      continue: "متابعة",
      successTitle: "اكتمل التسجيل",
      successSubtitle: "تم إنشاء حسابك بنجاح ويمكنك البدء في استخدام التطبيق الآن.",
      successContinue: "متابعة",
      somethingWrong: "حدث خطأ ما!",
      failedToLoad: "فشل تحميل بيانات الملف الشخصي",
    },
  }[lang];

  useEffect(() => {
    const fetchProfile = async () => {
      setInitialLoading(true);
      try {
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profile = data.response;

          if (profile.language_preference) {
            localStorage.setItem("app_language", profile.language_preference);
            setLang(profile.language_preference);
          }

          if (profile.pre_profile_completed === true) {
            localStorage.setItem("pre_profile_completed", "true");
            setIsCompleted(true);
          } else {
            localStorage.setItem("pre_profile_completed", "false");
            setIsCompleted(false);
            setName(profile.name || "");
            setEmail(profile.email || "");

            if (profile.whatsapp_number) {
              const raw = profile.whatsapp_number.toString();
              const sanitized = raw.replace(/^\+?971/, "");
              setWhatsapp(sanitized);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        const currentLang = localStorage.getItem("app_language") || "en";
        const translations = {
          en: t.failedToLoad,
          ar: "فشل تحميل بيانات الملف الشخصي",
        };
        showToast(err.message || translations[currentLang], "error");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, [USER_BASE_URL, VERSION, t.failedToLoad]);

  const validateName = (value) => {
    const trimmed = value.trim();
    return trimmed.length >= 1 && trimmed.length <= 100;
  };

  const validateWhatsapp = (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return true;
    if (!/^[0-9]{9}$/.test(trimmed)) return false;
    return /^(50|52|54|55|56|58)/.test(trimmed);
  };

  const validateEmail = (value) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value.trim()) &&
    value.trim().length >= 5 &&
    value.trim().length <= 255;

  const validateForm = () => {
    const isValid =
      validateName(name) &&
      validateEmail(email) &&
      validateWhatsapp(whatsapp);

    if (!isValid) setThrowError(true);
    return isValid;
  };

  const handleSubmit = async () => {
    setThrowError(false);
    if (!validateForm()) return;

    setLoading(true);
    try {
      const body = {
        name: name.trim(),
        email: email.trim(),
      };

      if (whatsapp.trim()) {
        body.whatsapp_number = `971${whatsapp.trim()}`;
      }

      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/profile/complete`,
        "POST",
        JSON.stringify(body),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        if (data.response?.name) {
          localStorage.setItem("username", data.response.name);
        } else {
          localStorage.setItem("username", body.name);
        }
        localStorage.setItem("pre_profile_completed", "true");
        showToast(data.message || t.successTitle, "success");
        setIsCompleted(true);
      } else {
        showToast(data.message || t.somethingWrong, "error");
      }
    } catch (err) {
      console.error(err);
      showToast(err.message || t.somethingWrong, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate("/dashboard");
  };

  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Navbar is_back_arrow={false} />
        <div className="verification-status-container">
          <h2 className="title">{t.successTitle}</h2>
          <p className="subtitle">{t.successSubtitle}</p>
          <div className="verification-image-container">
            <img
              src={onboardingCompletedImage}
              alt="Onboarding Completed"
              className="verification-image"
            />
          </div>
        </div>
        <button onClick={handleContinue} className="continue-btn">
          {t.successContinue}
        </button>
      </div>
    );
  }

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Navbar is_back_arrow={false} />
      <div className="common-content">
        <h2 className="title">{t.title}</h2>
        <p className="subtitle">{t.subtitle}</p>
      </div>

      <form className="pc-form">
        <p className="form-field-title">{t.nameLabel}</p>
        <div className="input-field-div">
          <input
            type="text"
            className="pc-input"
            placeholder={t.namePlaceholder}
            maxLength={100}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setTouched((prev) => ({ ...prev, name: true }));
            }}
          />
          {(touched.name || throwError) && !validateName(name) && (
            <div className="error">{t.nameError}</div>
          )}
        </div>

        <p className="form-field-title">{t.whatsappLabel}</p>
        <div
          className={`input-container ${
            (touched.whatsapp || throwError) && !validateWhatsapp(whatsapp)
              ? "error"
              : ""
          }`}
        >
          <img className="country-code" src={uaeFlag} alt="UAE" />
          <input
            ref={phoneRef}
            type="text"
            maxLength={9}
            inputMode="numeric"
            placeholder={t.whatsappPlaceholder}
            value={whatsapp}
            onChange={(e) => {
              const digitsOnly = e.target.value.replace(/\D/g, "");
              if (/^[0-9]*$/.test(digitsOnly) && digitsOnly.length <= 9) {
                setWhatsapp(digitsOnly);
                if (digitsOnly.length === 9) {
                  const startsValid = /^(50|52|54|55|56|58)/.test(digitsOnly);
                  if (!startsValid) {
                    showToast(t.whatsappError, "error");
                    setWhatsapp("");
                  } else {
                    phoneRef.current?.blur();
                  }
                }
              }
              setTouched((prev) => ({ ...prev, whatsapp: true }));
            }}
          />
        </div>
        {(touched.whatsapp || throwError) && !validateWhatsapp(whatsapp) && (
          <div className="error">{t.whatsappError}</div>
        )}

        <p className="form-field-title">{t.emailLabel}</p>
        <div className="input-field-div">
          <input
            type="email"
            className="pc-input"
            placeholder={t.emailPlaceholder}
            maxLength={255}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value.trimStart());
              setTouched((prev) => ({ ...prev, email: true }));
            }}
          />
          {(touched.email || throwError) && !validateEmail(email) && (
            <div className="error">{t.emailError}</div>
          )}
        </div>
      </form>

      <button
        onClick={handleSubmit}
        type="button"
        className="continue-btn"
        disabled={loading}
      >
        {loading ? <span className="loader"></span> : t.continue}
      </button>
    </div>
  );
}